import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { ZonePickingMode } from "@redotech/redo-fulfillment-wms-common/wms-constants-enums";
import { resolveDefaultZoneId } from "@redotech/redo-fulfillment-wms-common/zone-resolution";
import { LocationId } from "@redotech/redo-inventory-id";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCallback, useMemo, useRef, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export interface LocationZonesState {
  enabled: boolean;
  selectionRequired: boolean;
  zones: { id: string; name: string }[];
  byId: Map<string, string>;
  pending: boolean;
  defaultZoneId: string | null;
  error: unknown;
  blocked: boolean;
  retry: () => void;
}

function useLastDefined<T>(value: T | undefined): T | undefined {
  const last = useRef<T | undefined>(undefined);
  if (value !== undefined) {
    last.current = value;
  }
  return last.current;
}

export function useLocationZones(
  locationId: LocationId | null,
): LocationZonesState {
  const team = useRequiredContext(TeamContext);
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const zonePickingMode =
    team?.settings?.outboundLabels?.wms?.pickSession?.zonePickingMode ??
    ZonePickingMode.NONE;

  const [refreshKey, setRefreshKey] = useState<symbol>(() => Symbol());
  const retry = useCallback(() => setRefreshKey(Symbol()), []);

  const layoutsLoad = useLoad(async () => {
    if (zonePickingMode === ZonePickingMode.NONE) {
      return null;
    }
    if (!locationId) {
      return null;
    }
    const { layouts } = await outboundLabelsRpcClient.listWarehouseLayouts({});
    return layouts;
  }, [outboundLabelsRpcClient, zonePickingMode, locationId, refreshKey]);

  const layouts = useLastDefined(layoutsLoad.value);

  const layout = useMemo(() => {
    if (!layouts) {
      return null;
    }
    return layouts.find((l) => l.crdbLocationId === locationId) ?? null;
  }, [layouts, locationId]);

  const zones: { id: string; name: string }[] = useMemo(() => {
    const grid = layout?.warehouse;
    if (!grid?.zones || !grid?.zoneSequence) {
      return [];
    }
    const result: { id: string; name: string }[] = [];
    for (const zoneId of grid.zoneSequence) {
      const zone = grid.zones[zoneId];
      if (zone) {
        result.push({ id: zoneId, name: zone.name });
      }
    }
    return result;
  }, [layout]);

  const byId = useMemo(() => {
    const map = new Map<string, string>();
    for (const zone of zones) {
      map.set(zone.id, zone.name);
    }
    return map;
  }, [zones]);

  const defaultZoneId = useMemo(() => {
    const grid = layout?.warehouse;
    if (!grid) {
      return null;
    }
    return resolveDefaultZoneId(grid);
  }, [layout]);

  const enabled = zonePickingMode !== ZonePickingMode.NONE && zones.length > 0;
  const selectionRequired = zonePickingMode === ZonePickingMode.SEQUENTIAL;
  const settled =
    layoutsLoad.value !== undefined || layoutsLoad.error !== undefined;
  const pending =
    zonePickingMode !== ZonePickingMode.NONE &&
    (layoutsLoad.pending || !settled);
  const loadFailed = layoutsLoad.error !== undefined && layouts == null;
  const blocked =
    selectionRequired &&
    locationId != null &&
    !pending &&
    (loadFailed || zones.length === 0);

  return {
    enabled,
    selectionRequired,
    zones,
    byId,
    pending,
    defaultZoneId,
    error: loadFailed ? layoutsLoad.error : undefined,
    blocked,
    retry,
  };
}
